import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShiftData } from "../../hooks/shiftData";
import axios from "../../api/axios";
import toast from "../sweetalert/Sweetalert";
import { destroySession } from "../../helpers/Session";
import axiosaim from "../../api/axiosaim";

const Employee = ({ departmentslug }) => {
    const aimtoken = sessionStorage.getItem("aimtoken") || "";
    const uname = sessionStorage.getItem("uname") || "";
    const role = sessionStorage.getItem("role") || "";

    const { data: manageddepartmentsAIM } = useQuery({
        queryKey: ["managedAIMDepartments", uname],
        queryFn: async () => {
          const res = await axiosaim.get(`getmanageddepartments`, {
            headers: { "X-Aim-Token": aimtoken },
          });
          return res.data;
        },
        enabled: !!uname && !!aimtoken,
        retry: 1,
    });

    const navigate = useNavigate();
    const { shiftData, setShiftData } = useShiftData();

    const timeoutRef = useRef(null);
    const accesstoken = sessionStorage.getItem('accesstoken');

    const [formSubmit, setFormSubmit] = useState(false);
    const [selectedShifts, setSelectedShifts] = useState({});
    const [activeEmployee, setActiveEmployee] = useState(null);
    const [changedEmployees, setChangedEmployees] = useState(new Set());

    const employeeShiftsKey = ['employeeshifts', { shiftData, departmentslug }];

    const { data: employeedata = [], isLoading: isLoadingEmployees, error: errorEmployees } = useQuery({
        queryKey: employeeShiftsKey,
        queryFn: async () => {
            const res = await axios.get(
                `shifts/departments?payperiod=${shiftData?.payperiod}&department=${departmentslug}`,
                { headers: { 'Authorization': `Bearer ${accesstoken}` } }
            );
            return res.data.response || [];
        },
        enabled: !!shiftData?.payperiod
    });

    useEffect(() => {
        if (errorEmployees) {
            if (errorEmployees.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    }, [errorEmployees]);

    useEffect(() => {
      if (employeedata?.employeeshifts) {
        const initialShifts = employeedata.employeeshifts.reduce(
          (acc, item) => {
            acc[item.id] = item.default_shift || "";
            return acc;
          },
          {}
        );
        setSelectedShifts(initialShifts);
      }
    }, [employeedata]);

    const handleShiftChange = (id, value) => {
        setSelectedShifts((prev) => ({
            ...prev,
            [id]: value,
        }));
        setChangedEmployees((prev) => new Set(prev).add(id)); // track modified employee
    };

    const handleSetCurrentEmployee = (id, uname, defaultShift) => {
        const role = sessionStorage.getItem('role') || '';
        if (role === 'staff' && !defaultShift) {
            toast.fire({ icon: 'warning', title: 'No shift has been assigned yet for this pay period. Please contact your manager.' });
            return;
        }
        setActiveEmployee(id);
        setShiftData('employee', uname);
    };

    const employeeShiftsMutation = useMutation({
        mutationFn: async (employeeShiftData) => {
            const res = await axios.post('shifts/departments', employeeShiftData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` },
            });
            return res.data.response;
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMsg = (error.response.data.response) ? error.response.data.response : 'Something went wrong while saving department employee shifts!';
            toast.fire({ icon: 'error', title: errorMsg });

            if (error.response.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        },
        onSuccess: async (response) => {
            setFormSubmit(false);
            toast.fire({ icon: 'success', title: response });
        },
    });
    
    const handleSubmit = (e) => {
      e.preventDefault();
      const formData = new FormData();
      formData.append("payperiod", shiftData?.payperiod);
      formData.append("creator", uname);
      formData.append("source", "employee");

      // only include changed employees
      employeedata?.employeeshifts?.forEach((item) => {
        if (changedEmployees.has(item.id)) {
          formData.append("uname[]", item.uname);
          formData.append("department[]", item.department);
          formData.append("shift[]", selectedShifts[item.id] || "");
        }
      });

      setFormSubmit(true);
      setShiftData("employee", null);

      employeeShiftsMutation.mutate(formData);
    };

    // STAFF-ONLY FILTER
    let filteredEmployees = employeedata?.employeeshifts || [];
    if (role === 'staff') {
        filteredEmployees = filteredEmployees.filter(emp => emp.uname === uname);
    }

    return (
        <div className="employees">
            <form onSubmit={handleSubmit}>
                <div className="panel">
                    <div className='panel-title'>
                        <h3>{role === "staff" ? "My Shift Schedule" : "Employees"}</h3>
                        {manageddepartmentsAIM?.length > 0 && (<button type="submit" disabled={formSubmit}>
                            {formSubmit
                                ? (<span>SAVING <i className="fa fa-spinner fa-spin"></i></span>)
                                : ('SAVE')}
                        </button>)}
                    </div>
                    <div className="panel-content">
                        <table className={isLoadingEmployees || formSubmit ? 'loading' : ''}>
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Shift</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    isLoadingEmployees ? (
                                        [...Array(10)].map((_, i) => (
                                            <tr key={i}>
                                                <td><span>Loading...</span></td>
                                                <td>
                                                    <span>
                                                        <select disabled>
                                                            <option value=""></option>
                                                        </select>
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        filteredEmployees.map((item) => (
                                            <tr key={item.id} className={shiftData?.employee && activeEmployee === item.id ? 'active' : ''}>
                                                <td
                                                    onClick={() => {
                                                        if (!formSubmit) handleSetCurrentEmployee(item.id, item.uname, item.default_shift);
                                                    }}
                                                >
                                                    <span>{item.name}</span>
                                                </td>
                                                <td>
                                                    <span>
                                                        <input type="hidden" name="uname[]" value={item.uname} />
                                                        <input type="hidden" name="department[]" value={item.department} />
                                                        <select
                                                            name="shift[]"
                                                            value={selectedShifts[item.id] || ''}
                                                            onChange={(e) => handleShiftChange(item.id, e.target.value)}
                                                            disabled={role === 'staff'}
                                                        >
                                                            <option value=""></option>
                                                            {employeedata?.shifts?.map((shift) => (
                                                                <option key={shift.id} value={shift.shift}>{shift.shift}</option>
                                                            ))}
                                                        </select>
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default Employee;
